import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Dashboard = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchApplications = async () => {
      setLoading(true);
      const token = localStorage.getItem('token');

      if (!token) {
        setError('Authorization token is missing');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch('https://tbido-website-api.azurewebsites.net/clientDashboard', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          setError(errorData.error || 'Failed to fetch applications');
          return;
        }

        const data = await response.json();
        setApplications(data.applications || []);
      } catch (err) {
        setError('Error fetching applications');
      } finally {
        setLoading(false);
      }
    };

    fetchApplications();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    toast.success('Logged out successfully');
    setTimeout(() => {
      window.close(); // Close the tab
    }, 1000);
  };

  const getStatusBadge = (status) => {
    const styles = {
      'Approved': 'bg-green-100 text-green-800 border-green-200',
      'Pending': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'Declined': 'bg-red-100 text-red-800 border-red-200'
    };
    return styles[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                Status <span className="font-normal">Dashboard</span>
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                View the current status and updates of your incubatee application
              </p>
            </div>
            <button
              onClick={handleLogout}
              className="px-6 py-2.5 bg-gradient-to-r from-red-600 to-blue-800 text-white rounded-lg font-medium hover:from-red-700 hover:to-blue-900 transition-all duration-200 shadow-md hover:shadow-lg"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-gray-200 rounded-full"></div>
              <div className="w-16 h-16 border-4 border-red-600 rounded-full absolute top-0 left-0 animate-spin border-t-transparent"></div>
            </div>
            <p className="mt-4 text-gray-600 font-medium">Loading applications...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mb-4">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-red-800 font-semibold">{error}</p>
          </div>
        ) : applications.length > 0 ? (
          <div className="space-y-6">
            {applications.map((application, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                {/* Card Header */}
                <div className="bg-gradient-to-r from-red-600 to-blue-800 px-6 py-4">
                  <h2 className="text-white font-semibold text-lg">
                    Event: {application.eventName}
                  </h2>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <tbody className="divide-y divide-gray-200">
                      {/* Startup Name Row */}
                      <tr className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 text-sm font-semibold text-gray-700 bg-gray-50 w-48">
                          Startup Name
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {application.startUpName}
                        </td>
                      </tr>

                      {/* Event Address Row */}
                      <tr className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 text-sm font-semibold text-gray-700 bg-gray-50">
                          Event Address
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {application.eventAddress || 'TBD'}
                        </td>
                      </tr>

                      {/* Company Address Row */}
                      <tr className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 text-sm font-semibold text-gray-700 bg-gray-50">
                          Company Address
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {application.address || 'N/A'}
                        </td>
                      </tr>

                      {/* Application Status Row */}
                      <tr className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 text-sm font-semibold text-gray-700 bg-gray-50">
                          Application Status
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${getStatusBadge(application.status)}`}>
                            {application.status}
                          </span>
                        </td>
                      </tr>

                      {/* Call for Incubatees - Cohort II Row */}
                      <tr className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 text-sm font-semibold text-gray-700 bg-gray-50">
                          Program
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          Call for Incubatees - Cohort II
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Card Footer */}
                <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Applied on:</span>{' '}
                      {new Date(application.dateRegistered || Date.now()).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </div>
                    {application.status === 'Approved' && (
                      <button className="px-6 py-2 bg-gradient-to-r from-red-600 to-blue-800 text-white rounded-lg text-sm font-medium hover:from-red-700 hover:to-blue-900 transition-all duration-200 shadow-sm">
                        View Details
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Applications Yet</h3>
            <p className="text-gray-600">You haven't submitted any applications. Apply to programs to see your status here.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;